import React, { useState, useEffect } from "react";
import API from "../services/api";

const StepPersonal = ({ formData, setFormData }) => {
  const [touched, setTouched]           = useState({});
  const [wards, setWards]               = useState([]);
  const [locations, setLocations]       = useState([]);
  const [subLocations, setSubLocations] = useState([]);

  const personal = formData?.personal || {};

  // Draft is managed by MultiStepForm — do NOT load/save it here too,
  // it causes double-write races and stale state restores.

  useEffect(() => {
    API.get("/wards")
      .then((res) => setWards(res.data))
      .catch((err) => console.error("Failed to load wards:", err));
  }, []);

  const handleWardChange = async (e) => {
    const wardId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      personal: { ...prev.personal, ward_id: wardId, location_id: "", sub_location_id: "" },
    }));
    if (!wardId) { setLocations([]); setSubLocations([]); return; }
    try {
      const res = await API.get(`/locations?ward=${wardId}`);
      setLocations(res.data);
      setSubLocations([]);
    } catch (err) {
      console.error("Failed to load locations:", err);
      setLocations([]);
      setSubLocations([]);
    }
  };

  const handleLocationChange = async (e) => {
    const locationId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      personal: { ...prev.personal, location_id: locationId, sub_location_id: "" },
    }));
    if (!locationId) { setSubLocations([]); return; }
    try {
      const res = await API.get(`/sublocations?location=${locationId}`);
      setSubLocations(res.data);
    } catch (err) {
      console.error("Failed to load sublocations:", err);
      setSubLocations([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, personal: { ...prev.personal, [name]: value } }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const errors = {
    fullName:             !personal.fullName,
    gender:               !personal.gender,
    dateOfBirth:          !personal.dateOfBirth,
    identificationType:   !personal.identificationType,
    identificationNumber: !personal.identificationNumber,
    ward_id:              !personal.ward_id,
    location_id:          !personal.location_id,
    sub_location_id:      !personal.sub_location_id,
    village:              !personal.village,
    schoolLevel:          !personal.schoolLevel,
  };

  const inputClass = (name) =>
    `w-full border-2 rounded-xl px-3 py-2.5 text-sm focus:outline-none transition-colors duration-150 ${
      touched[name] && errors[name]
        ? "border-red-400 focus:border-red-500 bg-red-50"
        : "border-gray-200 focus:border-primary bg-white hover:border-gray-300"
    } disabled:bg-gray-100 disabled:cursor-not-allowed`;

  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
  const errorText  = "text-xs text-red-500 mt-1 flex items-center gap-1";

  return (
    <section className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-xl p-4 sm:p-6 md:p-8 space-y-6">
      <div>
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Section A: Personal Information
        </h2>
        <p className="text-gray-500 text-sm">Please provide your personal details as they appear officially.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">

        {/* Full Name */}
        <div>
          <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
          <input
            type="text" name="fullName" value={personal.fullName || ""}
            onChange={handleChange} onBlur={handleBlur}
            placeholder="e.g. John Mwenda Kimathi"
            className={inputClass("fullName")}
          />
          {touched.fullName && errors.fullName && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Gender — values match backend genderMap exactly: MALE | FEMALE */}
        <div>
          <label className={labelClass}>Gender <span className="text-red-500">*</span></label>
          <select
            name="gender" value={personal.gender || ""}
            onChange={handleChange} onBlur={handleBlur}
            className={inputClass("gender")}
          >
            <option value="">Select Gender</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
          </select>
          {touched.gender && errors.gender && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Date of Birth */}
        <div>
          <label className={labelClass}>Date of Birth <span className="text-red-500">*</span></label>
          <input
            type="date" name="dateOfBirth" value={personal.dateOfBirth || ""}
            onChange={handleChange} onBlur={handleBlur}
            className={inputClass("dateOfBirth")}
          />
          {touched.dateOfBirth && errors.dateOfBirth && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Identification Type — values match backend idTypeMap exactly: NATIONAL_ID | BIRTH_CERT */}
        <div>
          <label className={labelClass}>Identification Type <span className="text-red-500">*</span></label>
          <select
            name="identificationType" value={personal.identificationType || ""}
            onChange={handleChange} onBlur={handleBlur}
            className={inputClass("identificationType")}
          >
            <option value="">Select ID Type</option>
            <option value="NATIONAL_ID">National ID</option>
            <option value="BIRTH_CERT">Birth Certificate</option>
          </select>
          {touched.identificationType && errors.identificationType && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
          {personal.identificationType === "" && (
            <p className="text-xs text-gray-400 mt-1">
              No National ID yet? Select Birth Certificate — common for younger students and first-years.
            </p>
          )}
        </div>

        {/* Identification Number */}
        <div>
          <label className={labelClass}>Identification Number <span className="text-red-500">*</span></label>
          <input
            type="text" name="identificationNumber" value={personal.identificationNumber || ""}
            onChange={handleChange} onBlur={handleBlur}
            placeholder="Enter your ID or certificate number"
            className={inputClass("identificationNumber")}
          />
          {touched.identificationNumber && errors.identificationNumber && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Ward */}
        <div>
          <label className={labelClass}>Ward <span className="text-red-500">*</span></label>
          <select
            name="ward_id" value={personal.ward_id || ""}
            onChange={handleWardChange} onBlur={handleBlur}
            className={inputClass("ward_id")}
          >
            <option value="">Select Ward</option>
            {wards.map((w) => <option key={w._id} value={w._id}>{w.name}</option>)}
          </select>
          {touched.ward_id && errors.ward_id && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Location */}
        <div>
          <label className={labelClass}>
            Location <span className="text-red-500">*</span>
            {!locations.length && personal.ward_id && (
              <span className="text-xs text-gray-400 ml-2 font-normal">Loading...</span>
            )}
          </label>
          <select
            name="location_id" value={personal.location_id || ""}
            onChange={handleLocationChange} onBlur={handleBlur}
            disabled={!locations.length}
            className={inputClass("location_id")}
          >
            <option value="">Select Location</option>
            {locations.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
          </select>
          {touched.location_id && errors.location_id && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Sub-Location */}
        <div>
          <label className={labelClass}>
            Sub-Location <span className="text-red-500">*</span>
            {!subLocations.length && personal.location_id && (
              <span className="text-xs text-gray-400 ml-2 font-normal">Loading...</span>
            )}
          </label>
          <select
            name="sub_location_id" value={personal.sub_location_id || ""}
            onChange={handleChange} onBlur={handleBlur}
            disabled={!subLocations.length}
            className={inputClass("sub_location_id")}
          >
            <option value="">Select Sub-Location</option>
            {subLocations.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
          {touched.sub_location_id && errors.sub_location_id && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* Village */}
        <div>
          <label className={labelClass}>Village <span className="text-red-500">*</span></label>
          <input
            type="text" name="village" value={personal.village || ""}
            onChange={handleChange} onBlur={handleBlur}
            placeholder="Enter your village name"
            className={inputClass("village")}
          />
          {touched.village && errors.village && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

        {/* School Level — values already match backend enum: SECONDARY | COLLEGE | UNIVERSITY */}
        <div>
          <label className={labelClass}>School Level <span className="text-red-500">*</span></label>
          <select
            name="schoolLevel" value={personal.schoolLevel || ""}
            onChange={handleChange} onBlur={handleBlur}
            className={inputClass("schoolLevel")}
          >
            <option value="">Select Level</option>
            <option value="SECONDARY">Secondary School</option>
            <option value="COLLEGE">College</option>
            <option value="UNIVERSITY">University</option>
          </select>
          {touched.schoolLevel && errors.schoolLevel && (
            <p className={errorText}><span>⚠</span> This field is required</p>
          )}
        </div>

      </div>
    </section>
  );
};

export default StepPersonal;